import { useParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { invoiceStatusMap } from "./AcqInvoicesPage";
import { AcqInvoiceSummary } from "./AcqInvoiceSummary";
import { AcqInvoiceNextStep } from "./AcqInvoiceNextStep";
import { AcqInvoiceLinesTable } from "./AcqInvoiceLinesTable";
import { AcqInvoiceConfirmDialogs } from "./AcqInvoiceConfirmDialogs";
import { useAcqInvoiceDetail } from "./useAcqInvoiceDetail";
import { Loader2 } from "lucide-react";

const formatCurrency = (n: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(n);

export default function AcqInvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const detail = useAcqInvoiceDetail(id);

  if (detail.loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!detail.invoice) {
    return <div className="py-20 text-center text-slate-500">Invoice not found</div>;
  }

  const invoice = detail.invoice;
  const cfg = invoiceStatusMap[invoice.status] || {
    variant: "neutral" as const,
    label: invoice.status,
  };

  return (
    <PageContainer width="wide">
      <PageHeader
        title={`Invoice ${invoice.invoiceNumber}`}
        description={invoice.vendor.name}
        breadcrumbItems={[
          { label: "Invoices", href: "/acquisitions/invoices" },
          { label: invoice.invoiceNumber },
        ]}
        showSearch={false}
        titleSize="md"
      />

      <div className="space-y-5">
        <AcqInvoiceSummary invoice={invoice} statusCfg={cfg} formatCurrency={formatCurrency} />
        <AcqInvoiceNextStep
          status={invoice.status}
          matching={detail.matching}
          markPaidLoading={detail.markPaidLoading}
          onMatch={detail.match}
          onMarkPaid={detail.markPaid}
          onCancel={() => detail.setCancelOpen(true)}
          onReverse={() => detail.setReverseOpen(true)}
        />
        <AcqInvoiceLinesTable
          lines={invoice.lines}
          totalAmount={Number(invoice.totalAmount)}
          formatCurrency={formatCurrency}
        />
        {invoice.notes ? (
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-900">Notes</h2>
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{invoice.notes}</p>
          </div>
        ) : null}
      </div>

      <AcqInvoiceConfirmDialogs
        reverseOpen={detail.reverseOpen}
        cancelOpen={detail.cancelOpen}
        reverseLoading={detail.reverseLoading}
        cancelLoading={detail.cancelLoading}
        onReverseOpenChange={detail.setReverseOpen}
        onCancelOpenChange={detail.setCancelOpen}
        onConfirmReverse={detail.reverseMatch}
        onConfirmCancel={detail.cancelDraft}
      />
    </PageContainer>
  );
}
